"use client";

import React from "react";
import {
  MapPin,
  Globe,
  Building2,
  Users,
  DollarSign,
  Calendar,
} from "lucide-react";
import type { RankedCandidate } from "@/lib/research/api-types";
import { MatchFactorBadge } from "./MatchFactorBadge";
import { CompanyDetailRow } from "./CompanyDetailRow";

interface CompanyCandidateCardProps {
  candidate: RankedCandidate;
  rank: number;
  onSelect: (candidate: RankedCandidate) => void;
  disabled?: boolean;
}

export const CompanyCandidateCard = React.memo(function CompanyCandidateCard({
  candidate,
  rank,
  onSelect,
  disabled = false,
}: CompanyCandidateCardProps) {
  const locationValue = [candidate.city, candidate.state, candidate.country]
    .filter(Boolean)
    .join(", ");

  return (
    <button
      type="button"
      onClick={() => onSelect(candidate)}
      disabled={disabled}
      className="w-full text-left p-4 border rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:border-blue-300 dark:hover:border-blue-800 disabled:opacity-50 transition"
    >
      {/* Header with rank, name, and score */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-xs font-bold">
              {rank}
            </span>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{candidate.name}</h3>
          </div>
        </div>
        <div className="text-right ml-2">
          <div className="text-2xl font-bold text-blue-600">{candidate.score}</div>
          <div className="text-xs text-gray-500">match score</div>
        </div>
      </div>

      {/* Match factors */}
      <div className="flex flex-wrap gap-2 mb-3">
        <MatchFactorBadge label="Location Match" matched={candidate.matchFactors.locationMatch} />
        <MatchFactorBadge label="Industry Match" matched={candidate.matchFactors.industryMatch} />
        <MatchFactorBadge label="Size Match" matched={candidate.matchFactors.sizeMatch} />
        <MatchFactorBadge label="Domain Match" matched={candidate.matchFactors.domainMatch} />
      </div>

      {/* AI Reasoning */}
      <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">AI Analysis:</div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{candidate.reasoning}</div>
      </div>

      {/* Company Details Grid */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        {locationValue && (
          <CompanyDetailRow
            icon={<MapPin />}
            label="Location"
            value={locationValue}
          />
        )}
        {candidate.industry && (
          <CompanyDetailRow
            icon={<Building2 />}
            label="Industry"
            value={candidate.industry}
          />
        )}
        {candidate.employee_count !== undefined && candidate.employee_count > 0 && (
          <CompanyDetailRow
            icon={<Users />}
            label="Employees"
            value={candidate.employee_count.toLocaleString()}
          />
        )}
        {candidate.annual_revenue !== undefined && candidate.annual_revenue > 0 && (
          <CompanyDetailRow
            icon={<DollarSign />}
            label="Revenue"
            value={`$${(candidate.annual_revenue / 1000000).toFixed(1)}M`}
          />
        )}
        {candidate.founded_year !== undefined && candidate.founded_year > 0 && (
          <CompanyDetailRow
            icon={<Calendar />}
            label="Founded"
            value={candidate.founded_year}
          />
        )}
        {candidate.website_url && (
          <div className="flex items-start gap-2 col-span-2">
            <Globe className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-500">Website</div>
              <a
                href={candidate.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline truncate text-sm block"
                onClick={(e) => e.stopPropagation()}
              >
                {candidate.website_url}
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      {candidate.description && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 mb-1">About</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{candidate.description}</div>
        </div>
      )}
    </button>
  );
});
